/**
 * Tablet-side persistent storage. We deliberately use localStorage (not
 * sessionStorage) so a tablet reboot or browser restart preserves pairing.
 * Keys are namespaced under `kiosk.` so the kiosk PWA can coexist on the
 * same origin as another future app without collision.
 */

const PREFIX = "kiosk.";

const isBrowser = (): boolean => typeof window !== "undefined";

export const get = (key: string): string | null => {
  if (!isBrowser()) return null;
  try {
    return window.localStorage.getItem(`${PREFIX}${key}`);
  } catch {
    return null;
  }
};

// All three mutators below surface failures via console.error rather
// than throwing. The kiosk usually runs in fullscreen kiosk-mode where
// DevTools is hard to reach, but an operator debugging a stuck pair
// (paired server-side, unpaired client-side) can attach via
// `chrome://inspect` from a dev laptop and see exactly which key
// failed. Throwing here would crash the pair flow mid-finalize and
// leave the tablet in a worse state than a silent no-op.
const PERM_HINT =
  "[kiosk] localStorage blocked — check Knox/Fully Kiosk site data permissions";

export const set = (key: string, value: string): void => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(`${PREFIX}${key}`, value);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`${PERM_HINT} (write)`, key, err);
  }
};

export const remove = (key: string): void => {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(`${PREFIX}${key}`);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`${PERM_HINT} (remove)`, key, err);
  }
};

export const wipe = (): void => {
  if (!isBrowser()) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(PREFIX)) keys.push(k);
    }
    keys.forEach((k) => window.localStorage.removeItem(k));
  } catch (err) {
    // wipe() is called on revocation; a silent failure would mean a
    // revoked kiosk keeps stale credentials cached.
    // eslint-disable-next-line no-console
    console.error(`${PERM_HINT} (wipe)`, err);
  }
};
